// tslint:disable: max-line-length
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { State } from '../interfaces/state';
import { CommercialIndustry } from '../interfaces/commercial-industry';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';
import { BusinessData } from '../model/business-data';
import { CommercialSociety } from '../interfaces/commercial-society';
import { AccountData } from '../model/account-data';
import { catchSputnikService } from '../utils/catch-sputnik-error';

@Injectable({
    providedIn: 'root'
})
export class SputnikService {
    private sputnikV1Url = `${environment.CONSTANTS.SPUTNIK_SERVICE_URL}/api/v1`;

    constructor(protected http: HttpClient) {
    }

    public async getUserCompletedData(userID: number): Promise<any> {
        return this.http.get(`${this.sputnikV1Url}/data-user-completed/${userID}`).toPromise();
    }

    public async getStates(): Promise<State[]> {
        return this.http.get<any>(`${this.sputnikV1Url}/states`)
            .pipe(map(({ data }) => (data as State[]))).toPromise();
    }

    public async getCommercialIndustries(): Promise<CommercialIndustry[]> {
        return this.http.get<any>(`${this.sputnikV1Url}/commercial-activities`)
            .pipe(map(({ data }) => (data as CommercialIndustry[]))).toPromise();
    }

    public async getCommercialSocieties(): Promise<CommercialSociety[]> {
        return this.http.get<any>(`${this.sputnikV1Url}/societies`)
            .pipe(map(({ data }) => (data as CommercialSociety[]))).toPromise();
    }

    public async resendCode(onboardingId: string): Promise<any> {
        return this.http.post<any>(`${this.sputnikV1Url}/phone`, { onboardingId }).pipe(catchSputnikService).toPromise();
    }

    public async verifyPhone(onboardingId: string, code: string): Promise<any> {
        return this.http.put<any>(`${this.sputnikV1Url}/phone`, { onboardingId, code }).pipe(catchSputnikService).toPromise();
    }

    public async updatePhone(onboardingId: string, phone: string): Promise<any> {
        return this.http.patch<any>(`${this.sputnikV1Url}/phone`, { onboardingId, phone }).pipe(catchSputnikService).toPromise();
    }

    public async updateCurp(onboardingId: string, curp: string): Promise<any> {
        return this.http.patch<any>(`${this.sputnikV1Url}/curp`, { onboardingId, curp }).pipe(catchSputnikService).toPromise();
    }

    public async updateBusinessData(onboardingId: string, businessData: BusinessData): Promise<any> {
        return this.http.put(`${this.sputnikV1Url}/business-data`, { onboardingId, ...businessData }).pipe(catchSputnikService).toPromise();
    }

    public async updateAccountData(onboardingId: string, accountData: AccountData): Promise<any> {
        const payload = {
            onboardingId,
            ...accountData,
            birthdate: accountData.birthdate.toDate(),
            first_surname: accountData.firstSurname,
            second_surname: accountData.secondSurname
        };
        return this.http.put(`${this.sputnikV1Url}/account-data`, payload).pipe(catchSputnikService).toPromise();
    }

    public async getOnboardingSteps(onboardingId: string): Promise<any> {
        return this.http.get(`${this.sputnikV1Url}/user-steps/${onboardingId}`).toPromise();
    }

    public async updateStep(onboardingId: string, skipped: boolean, step: string, aditionalData?: object): Promise<any> {
        const payload = {
            onboardingId,
            skipped,
            step,
            aditionalData
        };
        return this.http.put(`${this.sputnikV1Url}/user-steps`, payload).pipe(catchSputnikService).toPromise();
    }

    public async skipStep(onboardingId: string, step: string): Promise<any> {
        return this.http.patch(`${this.sputnikV1Url}/skip-step`, { onboardingId, step }).pipe(catchSputnikService).toPromise();
    }

    public async createDepositAccount(onboardingId: string, account: string, accountType: 'card' | 'clabe'): Promise<any> {
        const payload = { onboardingId, account, accountType };
        return await this.http.post(`${this.sputnikV1Url}/deposit-account`, payload).toPromise();
    }

    public async verifyBin(bin: string): Promise<any> {
        return this.http.post(`${this.sputnikV1Url}/verify-bin`, { bin }).pipe(catchSputnikService).toPromise();
    }

    public async verifyClabe(clabe: string): Promise<any> {
        return this.http.post(`${this.sputnikV1Url}/verify-clabe`, { clabe }).pipe(catchSputnikService).toPromise();
    }

    public async checkRepeatedPhone(phone: string, rToken: string): Promise<any> {
        return this.http.get(`${this.sputnikV1Url}/check-repeated-phone/${phone}/${rToken}`).pipe(catchSputnikService).toPromise();
    }

}
